import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Stack,
  Skeleton,
  LinearProgress,
} from "@mui/material";
import StoreIcon from "@mui/icons-material/Store";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTimeOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import { getHttpClient } from "src/services/rest-api";
import { HTTPService } from "src/services/httpservice";

interface DashboardProps {
  count: number;
  onClickCompanies: () => void;
}

interface TrialDashboard {
  active_trials: number;
  expiring_in_7d: number;
  expiring_in_3d: number;
  expiring_in_1d: number;
  expired_trials: number;
  converted: number;
  conversion_rate_pct: number;
}

interface AnalyticsEvent {
  event_uuid: string;
  event_name: string;
  category: "BILLING" | "ONBOARDING" | "FEATURE_USE" | "ENGAGEMENT" | "SYSTEM";
  company_uuid: string | null;
  properties?: {
    company_name?: string;
    [key: string]: any;
  };
  createdAt: string;
}

const CATEGORY_COLOR: Record<string, "primary" | "success" | "warning" | "info" | "default"> = {
  BILLING: "success",
  ONBOARDING: "info",
  FEATURE_USE: "primary",
  ENGAGEMENT: "warning",
  SYSTEM: "default",
};

const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  caption?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}> = ({ label, value, caption, icon, color, loading }) => (
  <Card variant="outlined" sx={{ p: 2.5, borderRadius: "14px", height: "100%", bgcolor: "#fff" }}>
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
      <Typography fontSize="12px" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
        {label}
      </Typography>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "9px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}1A`,
          color,
        }}
      >
        {icon}
      </Box>
    </Box>
    {loading ? (
      <Skeleton variant="text" width="50%" height={40} />
    ) : (
      <Typography fontSize="28px" fontWeight={800} letterSpacing="-0.5px" lineHeight={1.1}>
        {value}
      </Typography>
    )}
    {caption && (
      <Typography fontSize="11px" color="text.secondary" fontWeight={500} mt={0.5}>
        {caption}
      </Typography>
    )}
  </Card>
);

const formatRelative = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const formatActivity = (event: AnalyticsEvent) => {
  const companyName = event.properties?.company_name || "Unknown company";

  switch (event.event_name) {
    case "subscription.admin_activated":
      return {
        companyName,
        eventLabel: "Marked paid",
        detail: event.properties?.plan ? `Admin set ${event.properties.plan} plan active` : "Admin moved company to paid active",
        tone: {
          bg: "#ECFDF5",
          color: "#047857",
          border: "#A7F3D0",
        },
      };
    case "subscription.admin_updated":
      return {
        companyName,
        eventLabel: "Subscription updated",
        detail: event.properties?.new_status ? `Status: ${event.properties.new_status}` : "Admin updated subscription details",
        tone: {
          bg: "#EEF2FF",
          color: "#4338CA",
          border: "#C7D2FE",
        },
      };
    case "subscription.admin_expired":
      return {
        companyName,
        eventLabel: "Marked expired",
        detail: "Admin moved subscription to expired",
        tone: {
          bg: "#FEF2F2",
          color: "#B91C1C",
          border: "#FECACA",
        },
      };
    case "subscription.admin_suspended":
      return {
        companyName,
        eventLabel: "Suspended",
        detail: "Admin suspended company access",
        tone: {
          bg: "#FEF2F2",
          color: "#B91C1C",
          border: "#FECACA",
        },
      };
    case "subscription.admin_grace":
      return {
        companyName,
        eventLabel: "Grace period",
        detail: "Admin moved subscription to grace period",
        tone: {
          bg: "#FFFBEB",
          color: "#B45309",
          border: "#FDE68A",
        },
      };
    case "subscription.activated":
      return {
        companyName,
        eventLabel: "Subscription active",
        detail: event.properties?.plan ? `${event.properties.plan} plan activated` : "Paid subscription activated",
        tone: {
          bg: "#ECFDF5",
          color: "#047857",
          border: "#A7F3D0",
        },
      };
    case "subscription.verified":
      return {
        companyName,
        eventLabel: "Payment verified",
        detail: event.properties?.plan ? `${event.properties.plan} payment confirmed` : "Subscription payment confirmed",
        tone: {
          bg: "#ECFDF5",
          color: "#047857",
          border: "#A7F3D0",
        },
      };
    case "subscription.cancelled":
      return {
        companyName,
        eventLabel: "Subscription cancelled",
        detail: "Access continues until current billing period ends",
        tone: {
          bg: "#F8FAFC",
          color: "#475569",
          border: "#CBD5E1",
        },
      };
    case "payment.failed":
      return {
        companyName,
        eventLabel: "Payment failed",
        detail: event.properties?.amount ? `Failed amount: ${event.properties.amount}` : "Payment requires attention",
        tone: {
          bg: "#FEF2F2",
          color: "#B91C1C",
          border: "#FECACA",
        },
      };
    case "trial.expired":
      return {
        companyName,
        eventLabel: "Trial expired",
        detail: "Moved to expired status",
        tone: {
          bg: "#FEF2F2",
          color: "#B91C1C",
          border: "#FECACA",
        },
      };
    case "trial.admin_enabled":
      return {
        companyName,
        eventLabel: event.properties?.new_status === "TRIAL_FULL" ? "Trial full" : "Trial limited",
        detail: event.properties?.previous_status
          ? `Admin changed from ${event.properties.previous_status}`
          : "Admin enabled trial access",
        tone: {
          bg: "#E0F2FE",
          color: "#0369A1",
          border: "#BAE6FD",
        },
      };
    case "trial.started":
      return {
        companyName,
        eventLabel: "Trial started",
        detail: "New trial workspace activated",
        tone: {
          bg: "#ECFDF5",
          color: "#047857",
          border: "#A7F3D0",
        },
      };
    case "trial.ending":
      return {
        companyName,
        eventLabel: "Trial ending soon",
        detail: "Requires follow-up",
        tone: {
          bg: "#FFFBEB",
          color: "#B45309",
          border: "#FDE68A",
        },
      };
    default:
      return {
        companyName,
        eventLabel: event.event_name.replace(/\./g, " "),
        detail: event.company_uuid || "System event",
        tone: {
          bg: "#EEF2FF",
          color: "#4338CA",
          border: "#C7D2FE",
        },
      };
  }
};

const getCompanyInitial = (name: string) => {
  const cleaned = name.trim();
  return cleaned ? cleaned.charAt(0).toUpperCase() : "?";
};

const AdminDashbaordLayout = ({ count, onClickCompanies }: DashboardProps) => {
  const [trial, setTrial] = useState<TrialDashboard | null>(null);
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await getHttpClient(HTTPService.APPADMIN_TRIAL_DASHBOARD, "GET", null, null, null);
      setTrial(res.data?.data || null);
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
    try {
      const res = await getHttpClient(HTTPService.APPADMIN_ANALYTICS, "GET", null, { days: 30 }, null);
      setEvents((res.data?.data || []).slice(0, 12));
    } catch {
      /* non-fatal */
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Typography fontSize="22px" fontWeight={800} letterSpacing="-0.5px">
          Platform Overview
        </Typography>
        <Typography fontSize="13px" color="text.secondary">
          Trials, conversions, and recent activity across all companies
        </Typography>
      </Box>

      {/* KPI Row */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            variant="outlined"
            onClick={onClickCompanies}
            sx={{
              p: 2.5,
              borderRadius: "14px",
              height: "100%",
              bgcolor: "#fff",
              cursor: "pointer",
              transition: "all 180ms ease",
              "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 28px rgba(0,0,0,0.10)" },
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Typography fontSize="12px" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                Companies
              </Typography>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "9px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "primary.main",
                  color: "#fff",
                }}
              >
                <StoreIcon sx={{ fontSize: 18 }} />
              </Box>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography fontSize="28px" fontWeight={800} letterSpacing="-0.5px" lineHeight={1.1}>
                {count}
              </Typography>
              <ArrowForwardIcon sx={{ color: "text.disabled", fontSize: 18 }} />
            </Box>
            <Typography fontSize="11px" color="text.secondary" fontWeight={500} mt={0.5}>
              Registered — view all
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Active Trials"
            value={trial?.active_trials ?? "—"}
            caption={`${trial?.expiring_in_7d ?? 0} expiring in 7 days`}
            icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
            color="#F59E0B"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Converted"
            value={trial?.converted ?? "—"}
            caption="Active paid subscriptions"
            icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
            color="#10B981"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Conversion Rate"
            value={trial ? `${trial.conversion_rate_pct}%` : "—"}
            caption={`${trial?.expired_trials ?? 0} trials expired`}
            icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
            color="#6366F1"
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Trial Expiry Funnel */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ p: 3, borderRadius: "16px", height: "100%", bgcolor: "#fff" }}>
            <Typography fontSize="15px" fontWeight={700}>Trial Expiry Funnel</Typography>
            <Typography fontSize="12px" color="text.secondary" mb={2}>
              Active trials approaching expiry — prioritise outreach
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {loading ? (
              <Stack spacing={2}>
                {[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={36} sx={{ borderRadius: 1 }} />)}
              </Stack>
            ) : (
              <Stack spacing={2.25}>
                {[
                  { label: "Expiring in 7 days", value: trial?.expiring_in_7d ?? 0, color: "#F59E0B" },
                  { label: "Expiring in 3 days", value: trial?.expiring_in_3d ?? 0, color: "#F97316" },
                  { label: "Expiring in 1 day", value: trial?.expiring_in_1d ?? 0, color: "#EF4444" },
                ].map((row) => {
                  const total = trial?.active_trials || 0;
                  const pct = total > 0 ? Math.min(100, (row.value / total) * 100) : 0;
                  return (
                    <Box key={row.label}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography fontSize="13px" fontWeight={600}>{row.label}</Typography>
                        <Typography fontSize="13px" fontWeight={700}>{row.value}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 7,
                          borderRadius: 4,
                          bgcolor: "#F1F5F9",
                          "& .MuiLinearProgress-bar": { bgcolor: row.color, borderRadius: 4 },
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ p: 3, borderRadius: "16px", height: "100%", bgcolor: "#fff" }}>
            <Typography fontSize="15px" fontWeight={700}>Recent Activity</Typography>
            <Typography fontSize="12px" color="text.secondary" mb={2}>
              Latest tracked events (last 30 days)
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {eventsLoading ? (
              <Stack spacing={1.5} mt={1.5}>
                {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="text" height={28} />)}
              </Stack>
            ) : events.length === 0 ? (
              <Typography fontSize="13px" color="text.secondary" textAlign="center" py={4}>
                No activity recorded yet.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {events.map((ev) => {
                  const activity = formatActivity(ev);
                  return (
                    <Box
                      key={ev.event_uuid}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        px: 1.25,
                        py: 1.15,
                        border: "1px solid #E2E8F0",
                        borderRadius: "12px",
                        bgcolor: "#F8FAFC",
                        transition: "background-color 160ms ease, border-color 160ms ease",
                        "&:hover": {
                          bgcolor: "#FFFFFF",
                          borderColor: "#CBD5E1",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                            bgcolor: "#EEF2FF",
                            color: "#4338CA",
                            fontWeight: 800,
                            fontSize: "13px",
                          }}
                        >
                          {getCompanyInitial(activity.companyName)}
                        </Box>
                        <Box minWidth={0}>
                          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                            <Typography fontSize="13px" fontWeight={800} color="text.primary" noWrap>
                              {activity.companyName}
                            </Typography>
                            <Chip
                              label={activity.eventLabel}
                              size="small"
                              sx={{
                                height: 21,
                                borderRadius: "7px",
                                bgcolor: activity.tone.bg,
                                color: activity.tone.color,
                                border: `1px solid ${activity.tone.border}`,
                                fontSize: "10.5px",
                                fontWeight: 800,
                                "& .MuiChip-label": { px: 0.85 },
                              }}
                            />
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.75} minWidth={0} mt={0.25}>
                            <Chip
                              label={ev.category}
                              size="small"
                              color={CATEGORY_COLOR[ev.category]}
                              sx={{ fontWeight: 700, fontSize: "9.5px", height: 18, borderRadius: "6px" }}
                            />
                            <Typography fontSize="11.5px" color="text.secondary" noWrap>
                              {activity.detail}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Typography fontSize="11px" color="text.disabled" whiteSpace="nowrap" fontWeight={600}>
                        {formatRelative(ev.createdAt)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashbaordLayout;
